// Library Methods
import { useEffect, useMemo, useRef } from "react";

// Utils
import { getImageInfo } from "src/utils/exams/examReportsHelper";

const DEFAULT_CONTAINER_SIZE = 312;

const GRID_COLUMNS = 14;
const GRID_ROWS = 14;

type QuadrantBuilderType = {
  gridValues?: Array<Array<number>>;
  mirrorHorizontally?: boolean;
  mirrorVertically?: boolean;
  containerSize?: number;
};

// Only ~11 distinct grayscale images exist but get reused across hundreds
// of cells, so cache the decoded <img> by src instead of reloading per cell.
const imageCache = new Map<string, Promise<HTMLImageElement>>();

const loadImage = (src: string): Promise<HTMLImageElement> => {
  const cached = imageCache.get(src);
  if (cached) return cached;

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

const QuadrantBuilder = ({
  gridValues = [], // should be an array of number arrays
  mirrorHorizontally = false,
  mirrorVertically = false,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: QuadrantBuilderType) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const spotWidth = useMemo(() => containerSize / 33, [containerSize]);
  const spotHeight = spotWidth;

  // Mirror the grid values with reverse() operations to prevent using scaleX/scaleY
  // (scaleX and scaleY introduced rasterization of the images, causing seams along each tile)
  const orderedGridValues = useMemo(() => {
    let result = gridValues;

    if (mirrorVertically) {
      result = [...result].reverse();
    }
    if (mirrorHorizontally) {
      result = result.map((row) => [...row].reverse());
    }

    return result;
  }, [gridValues, mirrorHorizontally, mirrorVertically]);

  // Screen-reader summary, since the canvas below has no per-cell DOM nodes
  // to carry alt text.
  const summary = useMemo(() => {
    const counts = new Map<string, number>();

    orderedGridValues.forEach((row) =>
      row.forEach((item) => {
        const { alt } = getImageInfo(item);
        counts.set(alt, (counts.get(alt) ?? 0) + 1);
      })
    );

    return Array.from(counts.entries())
      .map(([alt, count]) => `${count} ${alt}`)
      .join(" ");
  }, [orderedGridValues]);

  // Draws every tile onto a single canvas instead of 196 separate <img>
  // elements. html2canvas-pro leaves a seam wherever two different images meet
  // so give it one flattened image instead
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cancelled = false;

    const draw = async () => {
      const dpr = window.devicePixelRatio || 1;
      const cssWidth = GRID_COLUMNS * spotWidth;
      const cssHeight = GRID_ROWS * spotHeight;

      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);

      const ctx = canvas.getContext("2d");
      if (!ctx) return; // jsdom doesn't implement canvas rendering

      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = "#FFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Round each boundary once so adjacent tiles butt up exactly, no gap.
      const colBoundaries = Array.from({ length: GRID_COLUMNS + 1 }, (_, i) =>
        Math.round(i * spotWidth * dpr)
      );
      const rowBoundaries = Array.from({ length: GRID_ROWS + 1 }, (_, i) =>
        Math.round(i * spotHeight * dpr)
      );

      const uniqueSrcs = new Set<string>();
      orderedGridValues.forEach((row) =>
        row.forEach((item) => uniqueSrcs.add(getImageInfo(item).imageSrc))
      );

      const loadedEntries = await Promise.all(
        Array.from(uniqueSrcs).map(
          async (src) => [src, await loadImage(src)] as const
        )
      );
      if (cancelled) return;

      const imagesBySrc = new Map(loadedEntries);

      orderedGridValues.forEach((row, rowIndex) => {
        row.forEach((item, colIndex) => {
          const image = imagesBySrc.get(getImageInfo(item).imageSrc);
          if (!image) return;

          const x = colBoundaries[colIndex];
          const y = rowBoundaries[rowIndex];
          const width = colBoundaries[colIndex + 1] - x;
          const height = rowBoundaries[rowIndex + 1] - y;

          ctx.drawImage(image, x, y, width, height);
        });
      });
    };

    draw();

    return () => {
      cancelled = true;
    };
  }, [orderedGridValues, spotWidth, spotHeight]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="quadrant-canvas"
      role="img"
      aria-label={summary}
      style={{ display: "block" }}
    />
  );
};

export default QuadrantBuilder;
